import type { FunctionComponent } from 'react';

import { isB2B, isGermanyPlatform, isUkPlatform } from '@/core/settings/utils';
import { Link } from '@/domains/core/routing/components/Link';
import { Button } from '@/domains/core/tamagoshiTailwind/components/Button/Button';
import { Typography } from '@/domains/core/tamagoshiTailwind/components/Typography/Typography';
import { twMerge } from '@/domains/core/tamagoshiTailwind/util/twMerge';
import { Gtm } from '@/domains/core/tracking/utils/Gtm';
import { manageConsents } from '@/domains/legal/GDPR/utils/manageConsents';
import { GTM_CLICK_ON_LEGAL_MENTIONS } from '@/domains/retention/Footer/tracking/gtmEvents';

import {
  footerLegalConsumptionInformation,
  footerLegalConsumptionInformationB2B,
  footerLegalConsumptionInformationHref,
  footerLegalConsumptionInformationHrefB2B,
  footerLegalManageCookies,
  footerLegalMentions,
  footerLegalMentionsHref,
  footerLegalMentionsHrefB2B,
  footerLegalProtection,
  footerLegalProtectionHref,
  footerLegalReports,
  footerLegalReportsHref,
  footerLegalTerms,
  footerLegalTermsHref,
  footerLegalTermsHrefB2B,
  footerLegalTrustCenter,
  footerLegalTrustCenterHref,
  footerSitemap,
  footerSitemapHref,
} from '../translations';

export const LegalDataCommons: FunctionComponent = () => (
  <>
    <Typography data-testid="copyrightElm" scale="body2">
      {`©${new Date().getFullYear()} ManoMano`}
    </Typography>

    <div
      className={twMerge(
        `mt-s flex flex-wrap gap-x-m gap-y-s text-foundation-inverted
        tablet:justify-center`,
      )}
    >
      <Button
        text={footerLegalManageCookies}
        title={footerLegalManageCookies}
        onClick={() => {
          manageConsents();
          Gtm.push(
            GTM_CLICK_ON_LEGAL_MENTIONS({ id: footerLegalManageCookies }),
          );
        }}
        variant="link"
        size="M"
      />

      <Typography scale="body2">
        <Link
          href={footerSitemapHref}
          data-testid="siteMapCta"
          key={footerSitemap}
          onClick={() =>
            Gtm.push(
              GTM_CLICK_ON_LEGAL_MENTIONS({
                id: footerSitemap,
                url: footerSitemapHref,
              }),
            )
          }
        >
          {footerSitemap}
        </Link>
      </Typography>

      <Typography scale="body2">
        <Link
          href={isB2B() ? footerLegalMentionsHrefB2B : footerLegalMentionsHref}
          target="_blank"
          data-testid="legalCta"
          key={footerLegalMentions}
          onClick={() =>
            Gtm.push(
              GTM_CLICK_ON_LEGAL_MENTIONS({
                id: footerLegalMentions,
                url: isB2B()
                  ? footerLegalMentionsHrefB2B
                  : footerLegalMentionsHref,
              }),
            )
          }
        >
          {footerLegalMentions}
        </Link>
      </Typography>

      <Typography scale="body2">
        <Link
          href={footerLegalProtectionHref}
          data-testid="dataProtectCta"
          key={footerLegalProtection}
          target="_blank"
          onClick={() =>
            Gtm.push(
              GTM_CLICK_ON_LEGAL_MENTIONS({
                id: footerLegalProtection,
                url: footerLegalProtectionHref,
              }),
            )
          }
        >
          {footerLegalProtection}
        </Link>
      </Typography>

      <Typography scale="body2">
        <Link
          href={footerLegalTrustCenterHref}
          data-testid="dataTrustCenterCta"
          key={footerLegalTrustCenter}
          target="_blank"
          onClick={() =>
            Gtm.push(
              GTM_CLICK_ON_LEGAL_MENTIONS({
                id: footerLegalTrustCenter,
                url: footerLegalTrustCenterHref,
              }),
            )
          }
        >
          {footerLegalTrustCenter}
        </Link>
      </Typography>

      <Typography scale="body2">
        <Link
          href={isB2B() ? footerLegalTermsHrefB2B : footerLegalTermsHref}
          data-testid="termsAndConditionsCta"
          key={footerLegalTerms}
          target="_blank"
          onClick={() =>
            Gtm.push(
              GTM_CLICK_ON_LEGAL_MENTIONS({
                id: footerLegalTerms,
                url: isB2B() ? footerLegalTermsHrefB2B : footerLegalTermsHref,
              }),
            )
          }
        >
          {footerLegalTerms}
        </Link>
      </Typography>

      <Typography scale="body2">
        <Link
          href={
            isB2B()
              ? footerLegalConsumptionInformationHrefB2B
              : footerLegalConsumptionInformationHref
          }
          target="_blank"
          data-testid="consumptionInformationCta"
          key={footerLegalConsumptionInformation}
          onClick={() =>
            Gtm.push(
              GTM_CLICK_ON_LEGAL_MENTIONS({
                id:
                  isB2B() && isGermanyPlatform()
                    ? footerLegalConsumptionInformationB2B
                    : footerLegalConsumptionInformation,
                url: isB2B()
                  ? footerLegalConsumptionInformationHrefB2B
                  : footerLegalConsumptionInformationHref,
              }),
            )
          }
        >
          {isB2B() && isGermanyPlatform()
            ? footerLegalConsumptionInformationB2B
            : footerLegalConsumptionInformation}
        </Link>
      </Typography>

      {!isUkPlatform() && (
        <Typography scale="body2">
          <Link
            href={footerLegalReportsHref}
            data-testid="reportsCta"
            key={footerLegalReportsHref}
            onClick={() =>
              Gtm.push(
                GTM_CLICK_ON_LEGAL_MENTIONS({
                  id: footerLegalReports,
                  url: footerLegalReportsHref,
                }),
              )
            }
          >
            {footerLegalReports}
          </Link>
        </Typography>
      )}
    </div>
  </>
);

LegalDataCommons.displayName = 'LegalDataCommons';
